import api from './api';
import { cloneDeep } from 'lodash';

const state = {
	setup: {},
	error: false,
	initialLoad: true,
	message: '',
	reload: 0,
	status: {},
	allModels: [],
	userGroups: [],
	defaultLabels: [],
};

const getters = {
	setup: (state, getters, rootState, rootGetters) => {
		return state.setup;
	},
	getTimezone: (state) => state.setup.planning_timezone,
};

const mutations = {
	setSetup(state, { setup }) {
		state.setup = setup;
	},
	clearSetup(state) {
		state.setup = {};
	},
	setError(state, { message }) {
		(state.error = true), (state.message = message);
	},
	clearError(state) {
		(state.error = false), (state.message = '');
	},
	addVacation(state) {
		state.setup['vacation_days'].push({ amount: null, age: null, edit: true });
	},
	addWorkTime(state) {
		state.setup['weeklyWorktimeHistory'].push({ seconds: null, hours: null, validFrom: null, edit: true, new: true });
	},
	prepareWorkTime(state, { index, worktime }) {
		state.setup['weeklyWorktimeHistory'][index].seconds = worktime.hours * 3600;
		state.setup['weeklyWorktimeHistory'][index].validFrom = worktime.validFrom;
		delete state.setup['weeklyWorktimeHistory'][index].edit;
		delete state.setup['weeklyWorktimeHistory'][index].new;
		delete state.setup['weeklyWorktimeHistory'][index].hours;
	},
	deleteWorkTime(state, { index }) {
		state.setup['weeklyWorktimeHistory'].splice(index, 1);
	},
	prepareVacation(state, { index, vacation }) {
		delete state.setup['vacation_days'][index].edit;
		state.setup['vacation_days'][index].amount = vacation.amount;
		state.setup['vacation_days'][index].age = vacation.age;
	},
	deleteVacation(state, { index }) {
		state.setup['vacation_days'].splice(index, 1);
	},
	addExtraPayAccount(state) {
		const account = { name: null, abbreviation: null, number: null, edit: true, new: true };
		state.setup['extraPay_accounts'].push(account);
	},
	prepareExtraPayAccounts(state, { index, editedExtraPayAccounts }) {
		for (const account of state.setup['extraPay_accounts']) {
			delete account.edit;
			delete account.new;
		}
		state.setup['extraPay_accounts'][index].name = editedExtraPayAccounts[index].name;
		state.setup['extraPay_accounts'][index].abbreviation = editedExtraPayAccounts[index].abbreviation;
		state.setup['extraPay_accounts'][index].number = editedExtraPayAccounts[index].number;
	},
	deleteExtraPayAccount(state, { index }) {
		state.setup['extraPay_accounts'].splice(index, 1);
	},
	updateReloadCounter(state) {
		state.reload++;
	},
	setStatus(state, { status }) {
		state.status = status;
	},
	setAllModels(state, models) {
		state.allModels = models;
	},
	setUserGroups(state, { userGroups }) {
		state.userGroups = userGroups;
	},
	setRestAfterStandby(state, value) {
		state.setup['planning_restAfterStandby'] = value;
	},
	setDefaultLabels(state, { defaultLabels }) {
		state.defaultLabels = defaultLabels;
	},
	setInitialLoad(state) {
		state.initialLoad = false;
	},
};

const actions = {
	async load({ state, commit, rootGetters, dispatch }) {
		commit('clearError');
		commit('clearSetup'); // Clear setup to update the views correctly if previous values exist already
		try {
			await dispatch('loadStatus');
			await dispatch('loadSetup');
			await dispatch('loadUserGroups');
		} catch (err) {
			commit('setError', { message: err.response.data.message });
			return null;
		}
		commit('setInitialLoad');
	},

	async save({ state, commit, rootGetters }, settings) {
		if (state.initialLoad) {
			return; // Skip saving on reactive update during init
		}
		commit('setSetup', { setup: cloneDeep(settings) });
		const response = await api.save(settings);
		return response;
	},

	async getDatabaseExportUrl({ commit }, { models, anonymization }) {
		commit('clearError');
		try {
			return (await api.getDatabaseExportUrl(models, anonymization)).data;
		} catch (err) {
			commit('setError', { message: err.response.data.message });
			return null;
		}
	},

	async redisCleanup({ commit }) {
		commit('clearError');
		try {
			const response = await api.redisCleanup();
			return response.data;
		} catch (err) {
			const message = err.response.data.message;
			commit('setError', { message });
		}
	},

	async uploadDump({ commit }, { data, name, chunkNumber }) {
		return api.uploadDump(data, name, chunkNumber);
	},

	async importDatabase({ commit }, { name, originalFilename }) {
		return api.importDatabase(name, originalFilename);
	},

	addVacation({ commit }) {
		commit('addVacation');
	},

	addWorkTime({ commit }) {
		commit('addWorkTime');
	},

	checkWorkTime({ commit }, { worktime, index }) {
		if (worktime && !isNaN(parseFloat(worktime.hours)) && worktime.validFrom !== null) {
			commit('prepareWorkTime', { index, worktime });
			return true;
		}
		return false;
	},

	deleteWorkTime({ commit }, index) {
		commit('deleteWorkTime', { index });
	},

	checkVacation({ commit }, { vacation, index }) {
		if (vacation && !isNaN(parseInt(vacation.amount)) && !isNaN(parseInt(vacation.age))) {
			commit('prepareVacation', { index, vacation });
			return true;
		}
		return false;
	},

	deleteVacation({ commit }, index) {
		commit('deleteVacation', { index });
	},

	addExtraPayAccount({ commit }) {
		commit('addExtraPayAccount');
	},

	prepareExtraPayAccounts({ commit }, { editedExtraPayAccounts, index }) {
		commit('prepareExtraPayAccounts', { index, editedExtraPayAccounts });
	},

	deleteExtraPayAccount({ commit }, index) {
		commit('deleteExtraPayAccount', { index });
	},

	loadStatus({ commit }) {
		api.getStatus()
			.then((response) => {
				let status = response.data;
				commit('setStatus', { status });
			})
			.catch((err) => {
				console.log("Can't get status", err);
			});
	},

	async loadDefaultLabels({ commit }, ids) {
		const defaultLabels = (await api.listDefaultLabels(ids)).data;
		commit('setDefaultLabels', { defaultLabels });
	},

	async loadSetup({ commit }) {
		try {
			const setup = (await api.list()).data;
			commit('setSetup', { setup });
		} catch (err) {
			console.log(err);
		}
	},

	async loadAllModels({ commit }) {
		const models = (await api.listModels()).data;
		commit('setAllModels', models);
	},

	async loadUserGroups({ commit }) {
		const userGroups = (await api.listUserGroups()).data;
		commit('setUserGroups', { userGroups });
	},

	socket_pageReload({ state, commit }) {
		commit('updateReloadCounter');
	},

	socket_updated_Setup({ dispatch }) {
		dispatch('loadSetup');
	},
	updateRestAfterStandby({ commit }, value) {
		commit('setRestAfterStandby', value);
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
